// imports
import { createElement, FC } from 'react'
import { Box, Button } from '@bosch/react-frok'
import { useTranslation } from 'react-i18next'

// components
import { useMMAState } from '@/ui/advancedAssistants/MMA/useMMAState'
import { useMMAHandlers } from '@/ui/advancedAssistants/MMA/useMMAHandlers'
import HandoverModal, { HANDOVER_TYPE } from '@/ui/advancedAssistants/MMA/components/Handover/HandoverModal'
import CustomMarkdown from '@/ui/components/CustomMarkdown/CustomMarkdown'
import ChatImage from '@/ui/components/ChatImage/ChatImage'

// hooks & utils
import { useTransitionScreen } from '@/contexts/transitionContext'
import IconExtendSearch from '@/assets/icon-extend-search.svg'
import type XdcAdapter from '@/utils/ai-adapters/xdc'
import useWindowSize from '@/hooks/useWindowSize'
import useAssistantIllustration from '@/hooks/useAssistantIllustration'

// styles
import './FollowUpMessage.scss'

// Only the adapters of the XDC backend request the metadata
const isMetadataAdapter = (adapter: unknown): adapter is XdcAdapter =>
    typeof adapter === 'object'
    && adapter !== null
    && 'getMetadata' in adapter
    && typeof adapter.getMetadata === 'function'

interface FollowUpMessageProps {
    isDarkTheme?: boolean
    isGlobalSearchTriggered?: boolean
    onGlobalSearchClick?: () => void
    messageId?: string
}

const FollowUpMessage: FC<FollowUpMessageProps> = ({
    isDarkTheme = false,
    isGlobalSearchTriggered,
    onGlobalSearchClick,
    messageId,
}) => {
    const mmaState = useMMAState()
    const { t } = useTranslation()
    const { isMobile } = useWindowSize()
    const { startTransitionScreen } = useTransitionScreen()
    const { activeSpace, showModal, hideModal, changeConfirmButtonState, getGenericStoreValue } = mmaState
    const { image: assistantImage, backgroundColor: assistantImageBackgroundColor } = useAssistantIllustration()
    const { handlers } = useMMAHandlers(mmaState)

    const title = t('chat:ipnSearch:title')
    const description = isGlobalSearchTriggered
        ? t('chat:ipnSearch:descriptionGlobalSearch')
        : t('chat:ipnSearch:descriptionDefault')
    const markdownContent = `# ${title}\n${description}`

    const handleGlobalSearchClick = () => {
        onGlobalSearchClick?.()
    }

    const handleStartDocumentation = async () => {
        let selectedLine = { value: '', label: '' }
        const newAssistantId = `${activeSpace?.id}-documentation`
        const newAssistant = activeSpace?.assistants.find((assistant) => assistant.config.id === newAssistantId)
        const allParameters: Record<string, unknown> = getGenericStoreValue('mmaParameters') || {}

        if (newAssistant && !newAssistant?.metadata?.filter?.line) {
            const params = {
                ...allParameters,
                assistantEndpoint: newAssistant?.config.endpoint,
                object: 'chatFilter',
                space: activeSpace?.endpoint,
                token: await mmaState.getApiToken(),
            }

            if (!isMetadataAdapter(newAssistant.aiAdapter)) {
                throw new Error('Assistant does not support metadata requests')
            }
            const metadataResponse = await newAssistant.aiAdapter.getMetadata(params)
            if (metadataResponse) newAssistant.metadata = metadataResponse.metadata
        }

        const handleLineSelect = (lines: {value: string, label: string}[]) => {
            if (lines && lines.length > 0) {
                const lineLabels = lines.map(line => line.label).join(',')
                const lineValues = lines.map(line => line.value).join(', ')

                selectedLine = { value: lineValues, label: lineLabels }
                changeConfirmButtonState(!!selectedLine.value)
            } else {
                selectedLine = { value: '', label: '' }
                changeConfirmButtonState(false)
            }
        }

        changeConfirmButtonState(false)
        showModal({
            title: t('handover:title'),
            confirmLabel: t('general:buttons:confirm'),
            cancelLabel: t('general:buttons:cancel'),
            content: createElement(
                'div',
                { className: 'handover-modal' },
                createElement(HandoverModal, {
                    translate: t,
                    onSelect: handleLineSelect,
                    handoverType: HANDOVER_TYPE.NO_SOLUTION,
                })
            ),
            onConfirmClick: async () => {
                if (messageId) {
                    hideModal()
                    startTransitionScreen(async () => {
                        await handlers.handoverToAssistant(messageId, selectedLine)
                    })
                }
            },
            onCancelClick: hideModal,
        })
    }

    return (
        <div className="message-wrapper follow-up-message">
            <ChatImage
                image={assistantImage}
                isDarkTheme={isDarkTheme}
                backgroundColor={assistantImageBackgroundColor}
                size={isMobile ? 1.25 : 2}
                rounded
            />
            <Box className="message-box message-box-chatbot">
                <div className="follow-up-message-content">
                    <CustomMarkdown>
                        {markdownContent}
                    </CustomMarkdown>

                    <div className="follow-up-message-actions">
                        {!isGlobalSearchTriggered && (
                            <Button
                                className="action-button global-search-button"
                                onClick={handleGlobalSearchClick}
                                mode="integrated"
                            >
                                <div className="button-content">
                                    <img src={IconExtendSearch} alt={t('chat:ipnSearch:globalSearchButton')} />
                                    <span>{t('chat:ipnSearch:globalSearchButton')}</span>
                                </div>
                            </Button>
                        )}
                        <Button
                            className="action-button documentation-button"
                            onClick={handleStartDocumentation}
                            mode="integrated"
                            icon={{
                                iconName: 'document-edit',
                                title: t('chat:ipnSearch:documentationButton')
                            }}
                        >
                            {t('chat:ipnSearch:documentationButton')}
                        </Button>
                    </div>
                </div>
            </Box>
        </div>
    )
}

export default FollowUpMessage
